import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaFire, FaUsers, FaLaughSquint, FaBriefcase, FaShoppingCart } from 'react-icons/fa';
import useProductActions from '../../hooks/useProductActions';
import { COMMERCE_SOURCES } from '../../services/analyticsService';
import productosService from '../../services/productosService';

/* ─── paleta cómic ─── */
const C = {
  paper:   '#EBF8F2',
  teal:    '#1F9DA2',
  deep:    '#196665',
  ink:     '#1D1F1C',
  sand:    '#F4D081',
  gold:    '#D2AD66',
  mustard: '#E5D926',
  shadow:  '#482A18',
  cyan:    '#1BDDE1',
};

const comicBorder   = `3px solid ${C.ink}`;
const comicShadow   = `6px 6px 0px ${C.shadow}`;
const comicShadowSm = `4px 4px 0px ${C.shadow}`;

const GAME_IMAGE      = 'https://drive.google.com/thumbnail?id=1xhJRflF0nm3y_snLdfPxKyqXx0xGUGdX&sz=w800';
const HERO_BG_IMAGE   = '/images/ahilesdejo/hero-title-bg.png';
const GAME_PRODUCT_ID = 1239706;

const API_URL =
  process.env.REACT_APP_API_URL ||
  (window.location.origin.includes('localhost')
    ? 'http://localhost:8080/api'
    : `${window.location.origin}/api`);

export default function NovedadesSection() {
  const [gameProduct, setGameProduct] = useState(null);
  const { buyNow } = useProductActions(COMMERCE_SOURCES.HOME_NOVEDADES);

  useEffect(() => {
    fetch(`${API_URL}/productos/${GAME_PRODUCT_ID}`)
      .then(res => res.json())
      .then(data => {
        if (data && data.id) setGameProduct(productosService.normalizeProduct(data));
      })
      .catch(() => { /* no-op */ });
  }, []);

  // Delegado en useProductActions: emite el evento buy_now con su origen y,
  // a diferencia de antes, NO vacía el carrito del usuario.
  const handleBuyNow = () => buyNow(gameProduct, 1);

  return (
    <section
      id="novedades"
      className="pt-[calc(var(--navbar-height)+3rem)] pb-20 md:pt-[calc(var(--navbar-height)+5rem)] md:pb-28 relative overflow-hidden"
      style={{
        backgroundImage: `linear-gradient(135deg, rgba(31,157,162,0.93) 0%, rgba(25,102,101,0.96) 100%), url(${HERO_BG_IMAGE})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      {/* Patrón de puntos */}
      <div
        className="absolute inset-0 opacity-10 pointer-events-none"
        style={{
          backgroundImage: `radial-gradient(${C.ink} 1.5px, transparent 1.5px)`,
          backgroundSize: '20px 20px',
        }}
      />
      {/* Glow central */}
      <div
        className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[700px] h-[700px] rounded-full blur-3xl pointer-events-none"
        style={{ backgroundColor: C.cyan, opacity: 0.06 }}
      />

      <div className="container mx-auto px-4 relative z-10">

        {/* ── Encabezado ── */}
        <div className="text-center mb-10">
          <span
            className="inline-flex items-center gap-2 px-5 py-2 text-sm font-extrabold rounded-full mb-5"
            style={{ backgroundColor: C.mustard, color: C.ink, border: comicBorder, boxShadow: comicShadowSm }}
          >
            <FaFire className="animate-pulse" />
            NUEVO LANZAMIENTO
          </span>
          <h2
            className="font-cinzel text-4xl md:text-6xl font-extrabold mb-3 leading-tight"
            style={{
              color: C.sand,
              textShadow: `4px 4px 0px ${C.shadow}, -1px -1px 0 ${C.ink}, 1px -1px 0 ${C.ink}, -1px 1px 0 ${C.ink}`,
            }}
          >
            Ahí les dejo
          </h2>
          <p className="text-lg font-semibold max-w-xl mx-auto" style={{ color: `${C.paper}cc` }}>
            El juego de cartas que tu jefe no quiere que conozcas
          </p>
        </div>

        {/* ── Tarjeta principal ── */}
        <div
          className="max-w-5xl mx-auto rounded-3xl overflow-hidden"
          style={{ backgroundColor: C.paper, border: comicBorder, boxShadow: comicShadow }}
        >
          <div className="flex flex-col lg:flex-row">

            {/* ── Columna izquierda: imagen ── */}
            <div
              className="lg:w-5/12 p-8 lg:p-10 flex flex-col items-center justify-center gap-6"
              style={{
                background: `linear-gradient(160deg, ${C.deep} 0%, ${C.teal} 100%)`,
                borderRight: `3px solid ${C.ink}`,
              }}
            >
              <div className="relative w-52 h-52 md:w-64 md:h-64">
                <div
                  className="absolute inset-0 rounded-2xl rotate-3"
                  style={{ background: `${C.cyan}33` }}
                />
                <img
                  src={GAME_IMAGE}
                  alt="Ahí Les Dejo - Juego de cartas"
                  className="relative w-full h-full object-cover rounded-2xl"
                  style={{ border: comicBorder, boxShadow: comicShadowSm }}
                  loading="eager"
                />
              </div>

              {/* Highlights */}
              <div className="grid grid-cols-2 gap-3 w-full max-w-xs">
                <div className="flex items-center gap-2 rounded-xl p-3" style={{ backgroundColor: 'rgba(235,248,242,0.1)' }}>
                  <FaUsers className="flex-shrink-0" style={{ color: C.cyan }} />
                  <div>
                    <p className="text-white font-semibold text-xs">3–8 jugadores</p>
                    <p className="text-xs" style={{ color: `${C.paper}99` }}>After office</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 rounded-xl p-3" style={{ backgroundColor: 'rgba(235,248,242,0.1)' }}>
                  <FaLaughSquint className="flex-shrink-0" style={{ color: C.cyan }} />
                  <div>
                    <p className="text-white font-semibold text-xs">130 cartas</p>
                    <p className="text-xs" style={{ color: `${C.paper}99` }}>60 Trabajo + 70 Acción</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 rounded-xl p-3" style={{ backgroundColor: 'rgba(235,248,242,0.1)' }}>
                  <FaBriefcase className="flex-shrink-0" style={{ color: C.cyan }} />
                  <div>
                    <p className="text-white font-semibold text-xs">20–30 min</p>
                    <p className="text-xs" style={{ color: `${C.paper}99` }}>Partidas rápidas</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 rounded-xl p-3" style={{ backgroundColor: 'rgba(235,248,242,0.1)' }}>
                  <FaFire className="flex-shrink-0" style={{ color: C.cyan }} />
                  <div>
                    <p className="text-white font-semibold text-xs">24 tokens</p>
                    <p className="text-xs" style={{ color: `${C.paper}99` }}>Control de estrés</p>
                  </div>
                </div>
              </div>
            </div>

            {/* ── Columna derecha: copy + botones ── */}
            <div className="lg:w-7/12 p-8 lg:p-10 flex flex-col justify-center">
              <h3
                className="font-cinzel text-3xl md:text-4xl font-extrabold mb-3 leading-tight"
                style={{ color: C.deep }}
              >
                ¡Ya disponible!
              </h3>
              <p className="text-base font-semibold mb-2" style={{ color: C.teal }}>
                130 cartas + 24 tokens de estrés — la oficina más caótica de Colombia.
              </p>
              <p className="text-sm mb-6 leading-relaxed" style={{ color: C.ink }}>
                Gestiona el caos laboral, asigna tareas imposibles a tus compañeros y evita llegar
                a 10 puntos de estrés o tendrás que decir: <strong>&ldquo;¡Ahí les dejo!&rdquo;</strong>
                Ideal para after office, reuniones de amigos y cualquier ocasión donde quieras
                reírte de la vida corporativa.
              </p>

              {/* Mazos */}
              <div className="grid grid-cols-3 gap-3 mb-6">
                <div className="rounded-xl p-3 text-center" style={{ backgroundColor: `${C.cyan}14`, border: `1px solid ${C.cyan}55` }}>
                  <p className="text-xl mb-1" style={{ color: C.cyan }}>🏢</p>
                  <p className="font-bold text-xs" style={{ color: C.ink }}>Mazo Trabajo</p>
                  <p className="text-xs mt-0.5" style={{ color: C.deep }}>60 cartas</p>
                </div>
                <div className="rounded-xl p-3 text-center" style={{ backgroundColor: `${C.teal}22`, border: `1px solid ${C.teal}66` }}>
                  <p className="text-xl mb-1" style={{ color: C.sand }}>⚡</p>
                  <p className="font-bold text-xs" style={{ color: C.ink }}>Mazo Acción</p>
                  <p className="text-xs mt-0.5" style={{ color: C.deep }}>70 cartas</p>
                </div>
                <div className="rounded-xl p-3 text-center" style={{ backgroundColor: `${C.mustard}14`, border: `1px solid ${C.mustard}66` }}>
                  <p className="text-xl mb-1" style={{ color: C.mustard }}>🔥</p>
                  <p className="font-bold text-xs" style={{ color: C.ink }}>Tokens Estrés</p>
                  <p className="text-xs mt-0.5" style={{ color: C.deep }}>24 piezas</p>
                </div>
              </div>

              {/* Botones de acción */}
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={handleBuyNow}
                  className="flex-1 inline-flex items-center justify-center gap-2 font-extrabold py-3 px-6 rounded-xl transition-all transform hover:scale-105 text-sm"
                  style={{
                    backgroundColor: '#16a34a',
                    color: '#fff',
                    border: comicBorder,
                    boxShadow: comicShadowSm,
                  }}
                >
                  <FaShoppingCart />
                  Comprar ahora
                  {gameProduct && (
                    <span
                      className="ml-1 px-2 py-0.5 rounded-full text-xs font-extrabold"
                      style={{ backgroundColor: C.mustard, color: C.ink }}
                    >
                      ${gameProduct.precio_publico.toLocaleString('es-CO')}
                    </span>
                  )}
                </button>
                <Link
                  to="/ahilesdejo"
                  className="flex-1 inline-flex items-center justify-center gap-2 font-extrabold py-3 px-6 rounded-xl transition-all transform hover:scale-105 text-sm"
                  style={{
                    backgroundColor: C.sand,
                    color: C.ink,
                    border: comicBorder,
                    boxShadow: comicShadowSm,
                  }}
                >
                  Ver juego completo
                </Link>
              </div>
            </div>

          </div>
        </div>
      </div>
    </section>
  );
}
